import { createFileRoute, useNavigate, redirect } from "@tanstack/react-router";
import { z } from "zod";
import { LicenceForm } from "@/features/compliance/LicenceForm";
import { useCurrentUser, currentUserQueryOptions } from "@/hooks/useCurrentUser";
import { Card, CardContent } from "@/components/ui/card";
import { canCreateLicence } from "@/lib/roles";

const searchSchema = z.object({ assetId: z.string().optional() });

export const Route = createFileRoute("/_authenticated/compliance/licences/new")({
  validateSearch: searchSchema,
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(currentUserQueryOptions());
    if (!canCreateLicence(me.roles)) throw redirect({ to: "/compliance/licences" });
  },
  component: NewLicencePage,
});

const EDITOR_ROLES = new Set(["super_admin", "biomed_head", "facility_manager", "quality"]);

function NewLicencePage() {
  const { data: me } = useCurrentUser();
  const navigate = useNavigate();
  const { assetId } = Route.useSearch();
  if (!me) return null;
  if (!me.roles.some((r) => EDITOR_ROLES.has(r))) {
    return <Card className="mx-auto max-w-xl"><CardContent className="p-6">
      <h2 className="text-lg font-semibold">Permission required</h2>
      <p className="text-sm text-muted-foreground">You cannot create licences.</p>
    </CardContent></Card>;
  }
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">New Licence</h1>
      <LicenceForm
        prefilledAssetId={assetId ?? null}
        onSaved={() => {
          if (assetId) navigate({ to: "/assets/$assetId", params: { assetId } });
          else navigate({ to: "/compliance/licences" });
        }}
      />
    </div>
  );
}
